/** 
 * @props steps: array of object containing details of the steps for the stepper. sample below.
    steps = [
        {
            title: "Step Title 1",
            stepBodyComponent: <BodyComponent />,
            backButtonText: "Back",
            discardCb,
            prevCb: () => {},
            submitButtonText: "Next",
            submitCb: () => {},
            footerTextComponent: <span>Don’t know which option to choose?</span>
        }, {
           title: "Step Title 2",
            stepBodyComponent: <BodyComponent />,
            prevCb: () => {},
            submitButtonText: "Submit",
            submitCb: () => {},
        },
    ];

 * @props useSeparateUrl: set this prop to true if you want separate url for each step. (boolean) 
 * @props parentPath: send parent path in this prop if useSeparateUrl is set to true.
 *       parentPath = {"/componentdirectory"}
 * 
 * @props activeTabIndex: Pass index of the tab that you want to keep active when using separate url.
 * @props refreshStep: (number) - If there is a need to manually change the key to particular index then use this prop.
 * @props setRefreshStep: (state setter function) - Pass this function to have the same state as stepper
 * @props showTopRowButtons: (boolean) set this prop to true if you want stepper navigation buttons to be rendered at the top as well

*/

import React, { useEffect, useState } from 'react';
import styles from './StepperNavigation.styles';
import { Link } from 'react-router-dom';
import FooterWImageAndActionButtons from '../footerwimageandactionbuttons/FooterWImageAndActionButtons';
import CircularProgressBar from '../circularprogressbar/CircularProgressBar';
import TopRowStepperButtons from './components/TopRowStepperButtons';
import Switch from '../switch/Switch';
import { Theme } from '../constants';

export default function StepperNavigation({
    steps,
    useSeparateUrl = false,
    parentPath,
    activeTabIndex,
    progressBarContainerStyle = {},
    refreshStep = 0,
    setRefreshStep = () => { },
    customStepperBodyContainerStyles = {},
    customFooterBodyContainer = {},
    showTopRowButtons = false,
    customBodyMainStyles = {},
    showSwitchForOtherView = false,
    otherViewSwitchIsOpen = false,
    onSwitchToOtherViewCb = () => {},
    otherViewSwitchLabel = "",
    showTopSteps = true,
    showFooterImage = true,
    applyMaxHeightToBodyContainer = null,
}) {

    activeTabIndex = useSeparateUrl ? activeTabIndex : 0;
    const [activeStepIndex, setActiveStepIndex] = useState(activeTabIndex);

    // to set index at any particular value
    useEffect(() => {
        setActiveStepIndex(refreshStep)
    }, [refreshStep])

    const handlePrevButtonClick = (prevButtonClickCb) => {
        if(activeStepIndex !== 0){
            setActiveStepIndex((index) => index - 1);
            setRefreshStep((index) => index - 1);
        }
        prevButtonClickCb();
    }

    const handleNextButtonClick = (nextButtonClickCb) => {
        if (nextButtonClickCb !== undefined) {
            const result = nextButtonClickCb();
            if (result !== false) {
                if (activeStepIndex < steps.length - 1) {
                    setActiveStepIndex((index) => index + 1);
                    setRefreshStep((index) => index + 1);
                }
            }
        }
    }

    const percentage = steps.length > 1 ? Math.round((activeStepIndex / (steps.length - 1)) * 100) : 0;

    return (
        <>
            <div style={styles.stepper}>
                {
                    showTopSteps && <div style={styles.stepContainer}>
                        {
                            steps.map((item, idx) => {
                                return (
                                    <div key={idx} style={styles.step}>

                                        {useSeparateUrl ?
                                            (<Link to={`${parentPath}${item.linkTo}`} onClick={() => setActiveStepIndex(idx)}>
                                                <div style={{
                                                    ...styles.stepTitle,
                                                    ...(showTopRowButtons ? styles.stepTitlePaddingBottom : {}),
                                                    ...(idx === activeStepIndex ? styles.stepTitleActive : ""),
                                                    ...(idx < activeStepIndex ? styles.stepTitleCompleted : ""),
                                                }}
                                                >
                                                    <span style={styles.stepTitleText}>{item.title}</span>
                                                </div>
                                            </Link>
                                            ) : (
                                                <div style={{
                                                    ...styles.stepTitle,
                                                    ...(showTopRowButtons ? styles.stepTitlePaddingBottom : {}),
                                                    ...(idx === activeStepIndex ? styles.stepTitleActive : ""),
                                                    ...(idx < activeStepIndex ? styles.stepTitleCompleted : ""),
                                                }}
                                                >
                                                    <span style={styles.stepTitleText}>{item.title}</span>
                                                </div>
                                            )}

                                        <span style={{
                                            ...styles.stepLine,
                                            ...(idx === activeStepIndex ? styles.stepLineActive : ""),
                                            ...(idx < activeStepIndex ? styles.stepLineCompleted : ""),
                                        }}
                                        >
                                        </span>
                                    </div>
                                );
                            })
                        }
                    </div>
                }
                
                {
                    (showTopRowButtons || showSwitchForOtherView) ? <div style={styles.topRowStepperBtns}>
                        {
                            showSwitchForOtherView && <div style={styles.switchContainer}>
                                <span style={styles.switchSpan}>
                                    <Switch isOn={otherViewSwitchIsOpen} name={"otherViewSwitchOn"} switchUniqueId={"other-view-switch"} onColor={Theme.SECONDARY} handleToggleCb={onSwitchToOtherViewCb} customSwitchLabelStyle={styles.switchLabelCustomStyle} customSwitchButtonStyle={styles.switchButtonCustomStyle} />
                                </span>
                                <span style={styles.irsViewText}>{otherViewSwitchLabel}</span>
                        </div>
                        }
                        {
                            showTopRowButtons && <TopRowStepperButtons
                                steps={steps}
                                activeStepIndex={activeStepIndex}
                                handlePrevButtonClick={(prevCb) => handlePrevButtonClick(prevCb)}
                                handleNextButtonClick={(submitCb) => handleNextButtonClick(submitCb)}
                            />
                        }
                    </div>
                        : null
                }

                <div style={{ ...styles.stepperBodyContainer, ...customStepperBodyContainerStyles }}>
                    {
                        steps.map((item, idx) => {
                            return (
                                <div key={idx} style={{ ...styles.stepperBodyContent, ...(applyMaxHeightToBodyContainer ? {maxHeight: applyMaxHeightToBodyContainer} : {}), ...(idx === activeStepIndex ? { display: "flex" } : { display: "none" }) }}>
                                    <div style={{ ...styles.bodyMain, ...customBodyMainStyles }}>
                                        {item.stepBodyComponent}
                                    </div>
                                    {!item?.hideProgressBar &&
                                        <div style={{ ...styles.progressbarContainer, ...progressBarContainerStyle }}>

                                            <CircularProgressBar progress={percentage} width={"40px"} />
                                        </div>
                                    }
                                </div>
                            )
                        })
                    }
                </div>

                <div style={{ ...styles.footerBodyContainer, ...customFooterBodyContainer }}>
                    {
                        steps.map((item, idx) => {
                            return (
                                <div key={idx} style={{ ...styles.footerBodyContent, ...(idx === activeStepIndex ? { display: "flex" } : { display: "none" }) }}>
                                    <div style={styles.stepperFooter}>

                                        <FooterWImageAndActionButtons
                                            linkButton={{
                                                text: "Discard",
                                                clickCb: item.discardCb ? () => item.discardCb : null
                                            }}

                                            nextButton={{
                                                text: item.submitButtonText,
                                                clickCb: item.submitCb ? () => handleNextButtonClick(item.submitCb) : null,
                                                disabled: item.submitButtonDisabled,
                                                disabledInfoCb: item.submitButtonDisabledInfoCb
                                            }}

                                            backButton={{
                                                text: item.backButtonText,
                                                clickCb: item.prevCb ? () => handlePrevButtonClick(item.prevCb) : null
                                            }}
                                            showFooterImage={showFooterImage}
                                        >
                                            {item.footerTextComponent}
                                        </FooterWImageAndActionButtons>
                                    </div>
                                </div>)
                        })
                    }
                </div>
            </div>
        </>
    )
}